import { useCallback, useEffect, useState } from 'react';

import { Grid, Typography } from '@mui/material';

import { IOrderType } from '../../../types/OrderOverview';
import { ISigner } from '../../../types/Parties';
import { OrderStatus } from '../../constants/OrderStatus';
import { gray, primaryBlue, secondaryBlue } from '../../constants/colors';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import {
  SignerType,
  bySignerType,
  isSignerOfType,
} from '../../constants/solexSignerTypes';
import { IItemStatus } from '../../type';
import { formatDate, getPartyName } from '../../utils/helpers';
import ActionCardSALP from '../ActionCardSALP';
import Legend from '../Legend';
import OrdersActionMessageAlarm from '../OrdersActionMessageAlarm';

interface IProps {
  orderStatus: string;
  onClosingRef?: any;
  onClosingZIndex: number;
  orderType: IOrderType;
  orderExpired: boolean;
  isOrderReviewedBySA: boolean;
  isDocumentReviewedBySA: boolean;
  isAllInkSignDocumentsPrinted: boolean;
  isFacilitateInkSignConfirmed: boolean;
  docsTagged: boolean;
  docsEdited: boolean;
  signers: ISigner[];
  isCurrentUserEscrowOnOrder: boolean;
  isInkSignedDocsUploadedToVault: boolean;
  signingDate: any;
  onSaActionsClick: (name: string, item: IItemStatus, index: number) => void;
}

function SolexHybrideCloseActions(props: IProps) {
  const {
    orderStatus,
    orderType,
    onClosingRef,
    onClosingZIndex,
    orderExpired,
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    isAllInkSignDocumentsPrinted,
    isFacilitateInkSignConfirmed,
    isInkSignedDocsUploadedToVault,
    docsEdited,
    docsTagged,
    signers,
    isCurrentUserEscrowOnOrder,
    signingDate,
    onSaActionsClick,
  } = props;
  const closingDate = formatDate(signingDate, 'ddd, MMM DD, YYYY');
  const today = formatDate(new Date(), 'MM-DD-YYYY');
  const closingDateToCompare = formatDate(signingDate, 'MM-DD-YYYY');
  const closingBoxActive = new Date(closingDateToCompare) >= new Date(today);
  const PrintAllText =
    orderType.DocGen === DocGenerationSource.FA
      ? 'Print All Documents *'
      : 'Print All Ink Sign Documents *';

  const initialPreClosingItems: IItemStatus[] = [
    {
      name: 'Review Order Details and Party Information',
      disabled: false,
      completed: false,
    },
    { name: 'Review Document Package', disabled: false, completed: false },
    {
      name: 'Upload and Tag Title Documents',
      disabled: false,
      completed: false,
    },
    { name: PrintAllText, disabled: false, completed: false },
  ];

  const initialOnClosingItems: IItemStatus[] = [
    {
      name: 'Facilitate signing of Ink Sign Documents *',
      completed: false,
      disabled: false,
    },
  ];

  const initialPostClosingItems: IItemStatus[] = [
    {
      name:
        orderType.DocGen === DocGenerationSource.FA
          ? 'Upload Ink Sign Document to vault'
          : 'Upload Ink Sign Document to vault *',
      disabled: false,
      completed: false,
    },
  ];

  const [preClosingItems, setPreClosingItems] = useState<IItemStatus[]>(
    initialPreClosingItems
  );
  const [onClosingItems, setOnClosingItems] = useState<IItemStatus[]>(
    initialOnClosingItems
  );
  const [postClosingItems, setPostClosingItems] = useState<IItemStatus[]>(
    initialPostClosingItems
  );

  const orderStatusIsOpenLinkSent: boolean =
    orderStatus === OrderStatus.OpenLinksSent;
  const orderStatusIsOpenAllSigned: boolean =
    orderStatus === OrderStatus.OpenAllSigned;
  const orderStatusIsOpenAllPreviewed: boolean =
    orderStatus === OrderStatus.OpenAllPreviewed;
  const orderStatusIsCanceled: boolean = orderStatus === OrderStatus.Canceled;
  const orderStatusIsFailed: boolean =
    orderStatus === OrderStatus.Failed ||
    orderStatus === OrderStatus.OpenFailed;

  const hasCompletedSigning = (signer: ISigner) =>
    !!signer.SolexSigner?.SignerEvents.find((event) =>
      event.Name?.toLowerCase().includes('sign')
    );

  const getRelevantSigners: () => (ISigner & { signingDelegate?: ISigner })[] =
    useCallback(() => {
      return signers
        .filter(
          (signer) =>
            !isSignerOfType(signer, SignerType.PowerOfAttorney) &&
            !isSignerOfType(signer, SignerType.Lender)
        )
        .sort(bySignerType())
        .map((signer) => {
          if (signer.SigningDelegatePartyId) {
            const signingDelegate = signers.find(
              (signingDelegate) =>
                signingDelegate.Id === signer.SigningDelegatePartyId
            );
            if (signingDelegate) {
              return { ...signer, signingDelegate };
            } else {
              throw new Error('Signer with delegate should have POA');
            }
          }

          return signer;
        });
    }, [signers]);

  // set up pre closing items
  useEffect(() => {
    const updatedPreClosingSAItems: IItemStatus[] = [...preClosingItems];
    const reviewOrder = updatedPreClosingSAItems.find(
      (item) => item.name === 'Review Order Details and Party Information'
    );
    const reviewDocs = updatedPreClosingSAItems.find(
      (item) => item.name === 'Review Document Package'
    );
    const uploadTitleDocs = updatedPreClosingSAItems.find(
      (item) => item.name === 'Upload and Tag Title Documents'
    );
    const printDocuments = updatedPreClosingSAItems.find(
      (item) =>
        item.name === 'Print All Ink Sign Documents *' ||
        item.name === 'Print All Documents *'
    );

    if (!reviewOrder) return;
    reviewOrder.completed = isOrderReviewedBySA;
    reviewOrder.disabled = !(
      orderStatusIsOpenLinkSent ||
      orderStatusIsOpenAllSigned ||
      orderStatusIsOpenAllPreviewed
    );

    if (!reviewDocs) return;
    reviewDocs.completed = isDocumentReviewedBySA;
    reviewDocs.disabled = !(
      orderStatusIsOpenLinkSent ||
      orderStatusIsOpenAllSigned ||
      orderStatusIsOpenAllPreviewed
    );

    if (!uploadTitleDocs) return;
    uploadTitleDocs.completed = docsTagged && docsEdited;
    uploadTitleDocs.disabled =
      (docsTagged && docsEdited) ||
      !(
        orderStatusIsOpenLinkSent ||
        orderStatusIsOpenAllSigned ||
        orderStatusIsOpenAllPreviewed
      ) ||
      (!orderType.EditorStatus && orderType?.DocGen !== DocGenerationSource.FA);

    if (!printDocuments) return;
    printDocuments.completed = isAllInkSignDocumentsPrinted;
    printDocuments.disabled = !(
      orderStatusIsOpenLinkSent ||
      orderStatusIsOpenAllSigned ||
      orderStatusIsOpenAllPreviewed
    );

    setPreClosingItems(updatedPreClosingSAItems);
  }, [
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    docsTagged,
    docsEdited,
    orderStatus,
    isAllInkSignDocumentsPrinted,
  ]);

  useEffect(
    function populateOnClosingItems() {
      const isOnClosingDisabled =
        orderStatusIsOpenLinkSent &&
        orderStatusIsOpenAllSigned &&
        orderStatusIsOpenAllPreviewed;
      const items: IItemStatus[] = getRelevantSigners().map((signer) => {
        if (signer.signingDelegate) {
          return {
            name: `Please complete eSign session for ${getPartyName(
              signer
            )} by ${getPartyName(signer.signingDelegate)} (POA)`,
            completed: hasCompletedSigning(signer.signingDelegate),
            disabled: isOnClosingDisabled,
          };
        }

        return {
          name: `Please complete eSign session for ${getPartyName(signer)}`,
          completed: hasCompletedSigning(signer),
          disabled: isOnClosingDisabled,
        };
      });

      items.push({
        name: 'Facilitate signing of Ink Sign Documents *',
        completed: isFacilitateInkSignConfirmed,
        disabled: isOnClosingDisabled,
      });

      setOnClosingItems(items);
    },
    [orderStatus, isFacilitateInkSignConfirmed, signers]
  );

  // Set up post closing items
  useEffect(() => {
    const updatedOnClosingItems: IItemStatus[] = [...postClosingItems];
    const UploadAdditionalInkSigned = updatedOnClosingItems.find(
      (item) =>
        item.name === 'Upload Ink Sign Document to vault *' ||
        item.name === 'Upload Ink Sign Document to vault'
    );

    if (!UploadAdditionalInkSigned) return;
    UploadAdditionalInkSigned.completed = isInkSignedDocsUploadedToVault;
    UploadAdditionalInkSigned.disabled =
      isInkSignedDocsUploadedToVault ||
      orderExpired ||
      orderStatusIsCanceled ||
      orderStatusIsFailed;

    setPostClosingItems(updatedOnClosingItems);
  }, [orderStatus, isInkSignedDocsUploadedToVault, orderExpired]);

  const onOnClosingSignerClicked = (item: IItemStatus, index: number) => {
    if (item.name === 'Facilitate signing of Ink Sign Documents *') {
      onSaActionsClick('On Closing', item, index);
    } else {
      const signer = getRelevantSigners()[index];
      const linkToOpen = signer.signingDelegate
        ? signer.signingDelegate.SolexSigner?.SignerEvents[0].DocLink
        : signer.SolexSigner?.SignerEvents[0].DocLink;

      linkToOpen && window.open(linkToOpen, '_blank');
    }
  };

  return (
    <>
      <Grid
        container
        sx={{ backgroundColor: secondaryBlue, justifyContent: 'center' }}>
        <Grid
          item
          xs={1.5}
          sm={1.5}
          md={1.9}
          lg={1.55}
          xl={1.3}
          sx={{
            display: 'inline-block',
            alignSelf: 'center',
            borderRight: 1,
            borderColor: gray,
          }}>
          <Typography color={gray}>
            <strong>Order Type:</strong>
          </Typography>
          <Typography color={primaryBlue}>
            <strong>{orderType.Name}</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={9.5}
          sm={9.5}
          md={9}
          lg={9.5}
          xl={9.55}
          sx={{ paddingLeft: 1 }}>
          <OrdersActionMessageAlarm
            preClosingActive={closingBoxActive}
            onClosingActive={closingBoxActive}
            postClosingActive={closingBoxActive}
            isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
            preClosingSettlementItems={preClosingItems}
            onClosingSettlementItems={onClosingItems}
            postClosingSettlementItems={postClosingItems}
            orderStatus={orderStatus}
            orderType={orderType}
          />
        </Grid>
      </Grid>
      <Grid
        ref={onClosingRef}
        container
        sx={{
          backgroundColor: 'white',
          paddingTop: 3,
          paddingBottom: 4,
          justifyContent: 'center',
        }}>
        <ActionCardSALP
          active={closingBoxActive}
          cardItems={preClosingItems}
          title='Pre Closing'
          onSaActionsClick={(item, index) =>
            onSaActionsClick('Pre Closing', item, index)
          }
          id={'pre-closing'}
        />
        <ActionCardSALP
          active={closingBoxActive}
          cardItems={onClosingItems}
          title={'On Closing' + ' ('}
          closingDate={closingDate}
          zIndex={onClosingZIndex}
          onSaActionsClick={(item, index) =>
            onOnClosingSignerClicked(item, index)
          }
          id={'on-closing'}
        />
        <ActionCardSALP
          active={closingBoxActive}
          cardItems={postClosingItems}
          title='Post Closing'
          onSaActionsClick={(item, index) =>
            onSaActionsClick('Post Closing', item, index)
          }
          id={'post-closing'}
        />
        <Grid item xs={11}>
          <Legend />
        </Grid>
      </Grid>
    </>
  );
}

export default SolexHybrideCloseActions;
